import './Certificates.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { mockCertificates } from '../../mockCertificates';
import { Trans, useTranslation } from 'react-i18next';
import { ShopCertificate } from 'types/pricing';
import { ReactComponent as Document } from 'images/icons/DocumentThin.svg';
import { ReactComponent as Download } from 'images/icons/Download.svg';
import Paragraph from 'components/TextComps/Paragraph';
import Button from '@trackman/web-shared-components/components/Button/Button';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices } from 'index';
import certLevel1Icon from 'images/Certification/cert_lvl_1.png';
import certLevel2Icon from 'images/Certification/cert_lvl_2.png';
import { useCartStore } from 'stores/SubscriptionsAndPricing/CartStore';
import { CartItem } from 'types/cart';
import { AddedToCart } from '../../AddedToCart/AddedToCart';
import { LearnMoreDialog } from '../LearnMoreDialog/LearnMoreDialog';
import { SubscriptionPromptDialog } from '../SubscriptionPromptDialog/SubscriptionPromptDialog';
import { subscription } from '../Shop';
import { CartButton } from '../CartButton/CartButton';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';

export const Certificates = ({ isPremiumUser }: { isPremiumUser: boolean }) => {
  const { t } = useTranslation();
  const subscriptionsAndPricingService = useServices().subscriptionsAndPricingService;
  const certificatesFromApi = useQuery({
    queryKey: ['shopCertificationTests'],
    queryFn: subscriptionsAndPricingService.getShopCertificationTests,
    staleTime: Infinity,
  });

  const itemsInCart = useCartStore((state) => state.items);

  const certificates = mockCertificates;

  const addItemToCart = useCartStore((state) => state.addItemToCart);
  const isItemInCart = (certificateId: string) => itemsInCart.some((item) => item.id === certificateId);

  const [openedLearnMoreModalCertificateId, setOpenedLearnMoreModalCertificateId] = useState<string | null>(null);
  const [openedSubscriptionPromptModalCertificateId, setOpenedSubscriptionPromptModalCertificateId] = useState<string | null>(null);
  const [hasSubscriptionPromptModalBeenOpened, setHasSubscriptionPromptModalBeenOpened] = useState<boolean>(false);

  const handleAddToCart = useCallback(
    (item: CartItem) => {
      if (isPremiumUser || itemsInCart.some((cartItem) => cartItem.id === 'premium')) {
        addItemToCart(item);
        addCartItemToLocalStorage(item);
        return;
      } else {
        if (!hasSubscriptionPromptModalBeenOpened) {
          openSubscriptionPromptModal(item.id);
        } else {
          addItemToCart(item);
          addCartItemToLocalStorage(item);
        }
      }
    },
    [addItemToCart, hasSubscriptionPromptModalBeenOpened, isPremiumUser, itemsInCart]
  );

  const handleAddToCartFromSubscriptionPrompt = useCallback(
    (items: CartItem[]) => {
      items.forEach((item) => handleAddToCart(item));
      setOpenedSubscriptionPromptModalCertificateId(null);
    },
    [handleAddToCart]
  );

  const openSubscriptionPromptModal = useCallback((certificateId: string) => {
    setOpenedSubscriptionPromptModalCertificateId(certificateId);
    setHasSubscriptionPromptModalBeenOpened(true);
  }, []);

  return (
    <div className='shop-certificates'>
      {isPremiumUser ? <CartButton /> : null}
      <div className='shop-certificates-header'>
        <Subtitle>
          <>{t('EnhanceYourSkills', { ns: 'subscriptionsAndPricing' })}</>
        </Subtitle>
        <div className='shop-certificates-subtitle'>
          <span>{t('Get50%Off', { ns: 'subscriptionsAndPricing' })}</span>
          <Trans
            i18nKey='CompleteAll'
            ns='subscriptionsAndPricing'
            defaults='Complete all to become a <bold>Trackman Master</bold>.'
            components={{ bold: <strong /> }}
          ></Trans>
        </div>
      </div>

      <div className='shop-certificates-list'>
        {certificates.map((certificate, index) => {
          switch (certificate.status) {
            case 'completed':
              return <CompletedCertificate key={index} certificate={certificate} />;
            case 'owned':
              return <OwnedCertificate key={index} certificate={certificate} />;
            case 'unlocked':
              return (
                <>
                  <UnlockedCertificate
                    key={index}
                    certificate={certificate}
                    isPremiumUser={isPremiumUser}
                    isItemInCart={isItemInCart(certificate.id)}
                    addItemToCart={() =>
                      handleAddToCart({
                        id: certificate.id,
                        title: certificate.title,
                        basePrice: certificate.price,
                        discountedPrice: itemsInCart.some((item) => item.id === 'premium') ? certificate.price * 0.5 : undefined,
                      })
                    }
                    openLearnMoreDialog={setOpenedLearnMoreModalCertificateId}
                  />
                  {openedLearnMoreModalCertificateId === certificate.id ? (
                    <LearnMoreDialog
                      key={`learn-more-${index}`}
                      open={openedLearnMoreModalCertificateId === certificate.id}
                      certification={certificate}
                      isItemInCart={isItemInCart(certificate.id)}
                      isItemLocked={false}
                      onClose={() => setOpenedLearnMoreModalCertificateId(null)}
                      onAddToCart={() =>
                        handleAddToCart({
                          id: certificate.id,
                          title: certificate.title,
                          basePrice: certificate.price,
                          discountedPrice: itemsInCart.some((item) => item.id === 'premium') ? certificate.price * 0.5 : undefined,
                        })
                      }
                    />
                  ) : (
                    <></>
                  )}
                  {openedSubscriptionPromptModalCertificateId === certificate.id ? (
                    <SubscriptionPromptDialog
                      key={`subscription-prompt-${index}`}
                      open={openedSubscriptionPromptModalCertificateId === certificate.id}
                      onClose={() =>
                        handleAddToCartFromSubscriptionPrompt([
                          {
                            id: certificate.id,
                            title: certificate.title,
                            basePrice: certificate.price,
                            discountedPrice: itemsInCart.some((item) => item.id === 'premium') ? certificate.price * 0.5 : undefined,
                          },
                        ])
                      }
                      handleAddToCart={() =>
                        handleAddToCartFromSubscriptionPrompt([
                          {
                            id: certificate.id,
                            title: certificate.title,
                            basePrice: certificate.price,
                            discountedPrice: certificate.price * 0.5,
                          },
                          subscription,
                        ])
                      }
                    />
                  ) : (
                    <></>
                  )}
                </>
              );
            case 'locked':
              return (
                <>
                  <LockedCertificate
                    key={index}
                    certificate={certificate}
                    isPremiumUser={isPremiumUser}
                    openLearnMoreDialog={setOpenedLearnMoreModalCertificateId}
                  />
                  {openedLearnMoreModalCertificateId === certificate.id ? (
                    <LearnMoreDialog
                      key={`learn-more-${index}`}
                      open={openedLearnMoreModalCertificateId === certificate.id}
                      certification={certificate}
                      isItemInCart={isItemInCart(certificate.id)}
                      isItemLocked={true}
                      onClose={() => setOpenedLearnMoreModalCertificateId(null)}
                    />
                  ) : (
                    <></>
                  )}
                </>
              );
          }
        })}
      </div>
    </div>
  );
};

const BadgeImage = ({ badgeImageUrl, certificateTitle }: { badgeImageUrl: string; certificateTitle: string }) => {
  const getCertificationLevel = useMemo(
    () =>
      certificateTitle.toLowerCase().includes('level 1') ? 'level1' : certificateTitle.toLowerCase().includes('level 2') ? 'level2' : '',
    [certificateTitle]
  );

  return (
    <img
      src={badgeImageUrl ? badgeImageUrl : getCertificationLevel === 'level1' ? certLevel1Icon : certLevel2Icon}
      alt={certificateTitle ?? ''}
    />
  );
};

const CompletedCertificate = ({ certificate }: { certificate: ShopCertificate }) => {
  const { t } = useTranslation();
  const service = useServices().certificatesService;

  const getCertificationLevel = useMemo(
    () => (certificate.title.toLowerCase().includes('1') ? 'level1' : certificate.title.toLowerCase().includes('2') ? 'level2' : ''),
    [certificate.title]
  );

  const downloadPdf = useCallback(() => {
    const level = getCertificationLevel;
    service.download(level);
  }, [service, getCertificationLevel]);

  return (
    <div className='shop-certificate-card shop-certificate-card__completed'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.shortDescription}</>
        </Paragraph>
        <div>
          <div className='file-type'>
            <Document />
            <div>PDF</div>
          </div>
          <div>
            <Subtitle>
              <>{t('CertificationComplete', { ns: 'subscriptionsAndPricing' })}</>
            </Subtitle>
            <Paragraph>
              <>{t('DownloadYourCertificateHere', { ns: 'subscriptionsAndPricing' })}</>
            </Paragraph>
          </div>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button className='download-button' startIcon={<Download />} onClick={downloadPdf}>
          {t('DownloadPdf', { ns: 'subscriptionsAndPricing' })}
        </Button>
      </div>
    </div>
  );
};

const OwnedCertificate = ({ certificate }: { certificate: ShopCertificate }) => {
  const { t } = useTranslation();
  return (
    <div className='shop-certificate-card shop-certificate-card__owned'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.shortDescription}</>
        </Paragraph>
      </div>
      <div className='shop-certificate-actions'>
        <Button className='take-exam-button' intent='secondary'>
          {t('TakeCertificationExam', { ns: 'subscriptionsAndPricing' })}
        </Button>
      </div>
    </div>
  );
};

const UnlockedCertificate = ({
  certificate,
  isPremiumUser,
  isItemInCart,
  addItemToCart,
  openLearnMoreDialog,
}: {
  certificate: ShopCertificate;
  isPremiumUser: boolean;
  isItemInCart: boolean;
  addItemToCart: () => void;
  openLearnMoreDialog: (certificateId: string) => void;
}) => {
  const { t } = useTranslation();
  const certificatePrice = isPremiumUser ? certificate.price * 0.5 : certificate.price;
  const getPriceDescription = useMemo(() => {
    const currentLng: string = localStorage.i18nextLng || 'en-US';
    const descriptionPrice = isPremiumUser ? certificate.price : certificate.price * 0.5;
    return isPremiumUser
      ? t('PriceDescriptionPremiumUser', { ns: 'subscriptionsAndPricing', price: descriptionPrice })
      : t('PriceDescriptionFreeUser', { ns: 'subscriptionsAndPricing', price: descriptionPrice });
  }, [certificate.price, isPremiumUser, t]);

  return (
    <div className='shop-certificate-card shop-certificate-card__unlocked'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.shortDescription}</>
        </Paragraph>
        <div>
          <h2>${certificatePrice}</h2>
          <Paragraph>
            <>{getPriceDescription}</>
          </Paragraph>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button onClick={() => openLearnMoreDialog(certificate.id)}>{t('LearnMore', { ns: 'subscriptionsAndPricing' })}</Button>
        {!isItemInCart ? (
          <Button intent='primary' onClick={() => addItemToCart()}>
            {t('AddToCart', { ns: 'subscriptionsAndPricing' })}
          </Button>
        ) : (
          <AddedToCart />
        )}
      </div>
    </div>
  );
};

const LockedCertificate = ({
  certificate,
  isPremiumUser,
  openLearnMoreDialog,
}: {
  certificate: ShopCertificate;
  isPremiumUser: boolean;
  openLearnMoreDialog: (certificateId: string) => void;
}) => {
  const { t } = useTranslation();
  const certificatePrice = isPremiumUser ? certificate.price * 0.5 : certificate.price;
  const getPriceDescription = useMemo(() => {
    const currentLng: string = localStorage.i18nextLng || 'en-US';
    const descriptionPrice = isPremiumUser ? certificate.price : certificate.price * 0.5;
    return isPremiumUser
      ? t('PriceDescriptionPremiumUser', { ns: 'subscriptionsAndPricing', price: descriptionPrice })
      : t('PriceDescriptionFreeUser', { ns: 'subscriptionsAndPricing', price: descriptionPrice });
  }, [certificate.price, isPremiumUser, t]);

  return (
    <div className='shop-certificate-card shop-certificate-card__locked'>
      <div className='shop-certificate-card-badge'>
        <BadgeImage badgeImageUrl={certificate.badgeImageUrl} certificateTitle={certificate.title} />
      </div>
      <div className='shop-certificate-content'>
        <Subtitle className='certificate-title'>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.shortDescription}</>
        </Paragraph>
        <div>
          <h2>${certificatePrice}</h2>
          <Paragraph>
            <>{getPriceDescription}</>
          </Paragraph>
        </div>
      </div>
      <div className='shop-certificate-actions'>
        <Button className='learn-more-button__locked' onClick={() => openLearnMoreDialog(certificate.id)}>
          {t('LearnMore', { ns: 'subscriptionsAndPricing' })}
        </Button>
      </div>
    </div>
  );
};
